import * as Contacts from 'expo-contacts';
import * as Localization from 'expo-localization';
import countries from '@assets/countries.json';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { Linking, Platform } from 'react-native';

const regionCodes = Localization.getLocales().reduce<string[]>((acc, l) => {
  if (l.regionCode) {
    acc.push(l.regionCode);
  }
  return acc;
}, []);

export const DEFAULT_COUNTRY_CODE =
  // Use device country code if it exists in our data file, otherwise default
  // to the United States
  (regionCodes.find((regionCode) =>
    countries.some((c) => c.code === regionCode),
  ) as CountryCode) || 'US';

export const E164_FORMAT = 'E.164';

export const formatPhoneNumber = (text: string) => {
  const phoneNumber = parsePhoneNumberFromString(text);
  return phoneNumber && phoneNumber.isValid()
    ? phoneNumber.formatNational()
    : text;
};

export const getE164PhoneNumber = (
  text: string,
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
  allowInvalidNumber = false,
) => {
  const phoneNumber = parsePhoneNumberFromString(text, countryCode);
  const validPhoneNumber =
    phoneNumber && phoneNumber.isValid()
      ? phoneNumber.format(E164_FORMAT)
      : null;
  return allowInvalidNumber && !validPhoneNumber
    ? (phoneNumber?.format(E164_FORMAT) ?? text)
    : validPhoneNumber;
};

export const parseContactPhoneNumber = ({
  countryCode,
  digits,
  number,
}: Contacts.PhoneNumber) => {
  // TODO - continue investigating contact phone numbers on Android
  // TODO - support more than US if contact phone number is missing one
  const codeToUse = countryCode || DEFAULT_COUNTRY_CODE;
  const phoneNumber = parsePhoneNumberFromString(
    digits || number || '',
    codeToUse.toUpperCase() as CountryCode,
  );

  return phoneNumber?.isValid() ? phoneNumber : null;
};

/**
 * Checks if the given phone number is NOT a Mali phone number.
 * Mali phone numbers start with the country code +223.
 * @example
 * isNotMaliPhoneNumber('+223123456789'); // false, this is a Mali phone number
 * isNotMaliPhoneNumber('+1234567890'); // true, this is not a Mali phone number
 * isNotMaliPhoneNumber(null); // true, null is not a Mali phone number
 * isNotMaliPhoneNumber(undefined); // true, undefined is not a Mali phone number
 * isNotMaliPhoneNumber(''); // true, empty string is not a Mali phone number
 * @param {string | null | undefined} phoneNumber - The phone number to check.
 * @returns {boolean} - Returns true if the phone number is not a Mali phone number, otherwise false.
 */
export const isNotMaliPhoneNumber = (phoneNumber: string | null | undefined) =>
  !phoneNumber?.startsWith('+223');

export const onCallMobilePhone = async (phoneNumber: string) => {
  if (Platform.OS === 'android' || Platform.OS === 'web') {
    await Linking.openURL(`tel:${phoneNumber}`);
  }

  if (Platform.OS === 'ios') {
    await Linking.openURL(`telprompt:${phoneNumber}`);
  }
};

export const isValidPhoneNumber = (text: string | null | undefined) => {
  if (!text) {
    return false;
  }
  const phoneNumber = parsePhoneNumberFromString(text);
  return phoneNumber && phoneNumber.isValid() && isNotMaliPhoneNumber(text);
};
